<script lang="ts" setup>
import { useSdk } from '~/sdk';
import { SfLoaderCircular } from '@storefront-ui/vue';
import Button from '~/components/ui/button/Button.vue';

defineProps<{
  isOrderFree?: boolean;
}>();

const error = ref<string | null>(null);
const loading = ref(false);

const { t } = useI18n();
const agreed = ref(false);

const privacyDescription = computed(() => {
  const privacyTextLong = t('I agree to the terms of service and will adhere to them unconditionally.');
  const privacyTextShort = t('terms of service').toLocaleLowerCase();
  return privacyTextLong.replace(
    privacyTextShort,
    `<a target="_blank" class='text-indigo-500' href='/terms-of-sale'>${privacyTextShort}</a>`,
  );
});

async function confirmOrder() {
  if (!agreed.value) {
    return;
  }

  error.value = null;
  loading.value = true;

  try {
    const res: any = await useSdk().commerce.orderValidate({});

    if (res?.psdata?.order.details?.id) {
      location.href = `/checkout/success?id_order=${res.psdata.order.details.id}`;
    } else if (res?.success && res?.redirectUrl) {
      location.href = res.redirectUrl;
    } else {
      error.value = 'Failed to create order';
    }
  } catch (e: any) {
    error.value = e.message || 'An error occurred while confirming your order';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="py-4">
    <Alert v-if="error" variant="destructive" class="mb-4 w-full">
      <AlertDescription>
        {{ error }}
      </AlertDescription>
    </Alert>

    <Alert class="mb-4 w-full" v-if="isOrderFree">
      <AlertDescription>
        {{ t('Your order total is zero. You can confirm your order without payment.') }}
      </AlertDescription>
    </Alert>
    <Alert class="mb-4 w-full bg-orange-50" v-if="!isOrderFree">
      <AlertTitle class="text-sm font-bold">{{ t('important_notes') }}:</AlertTitle>
      <AlertDescription class="text-sm">
        <p class="mb-2">{{ t('wire_transfer_note.intro') }}</p>
        <ul class="list-disc pl-5 space-y-1 mb-2">
          <li><strong>{{ t('wire_transfer_note.bullet_1_bold') }}</strong> {{ t('wire_transfer_note.bullet_1_text') }}</li>
          <li><strong>{{ t('wire_transfer_note.bullet_2_bold') }}</strong> {{ t('wire_transfer_note.bullet_2_text') }}</li>
          <li><strong>{{ t('wire_transfer_note.bullet_3_bold') }}</strong> {{ t('wire_transfer_note.bullet_3_text') }}</li>
        </ul>
        <p class="font-bold italic">{{ t('wire_transfer_note.warning') }}</p>
      </AlertDescription>
    </Alert>

    <div class="relative flex items-start">
      <div class="flex h-6 items-center">
        <Checkbox v-model="agreed" id="privacy" aria-describedby="privacy-description" name="privacy" type="checkbox" />
      </div>
      <div class="ml-3 leading-6">
        <label for="privacy" class="font-medium text-gray-900">Privacy</label>
        <p v-html="privacyDescription" id="privacy-description" class="text-gray-500 text-sm"></p>
      </div>
    </div>

    <div class="my-6" v-if="!agreed">
      <Alert class="w-full !justify-start" variant="destructive">
        <AlertTitle>{{ t('Please make sure') }}</AlertTitle>
        <AlertDescription>
          <ul role="list" class="list-disc space-y-1 pl-5 my-2">
            <li>{{ t('Accepted the terms and conditions') }}</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>

    <div class="mt-6 flex justify-center">
      <Button 
        type="button" 
        class="w-full max-w-md"
        :disabled="!agreed || loading"
        @click="confirmOrder"
      >
        <SfLoaderCircular v-if="loading" class="mr-2" size="sm" />
        {{ t('Confirm Order') }}
      </Button>
    </div>
  </div>
</template>
